import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { fetchColumns } from '@/lists/api'
import { EntityListPage } from '@/lists/EntityListPage'
import { FilterBuilder } from '@/lists/FilterBuilder'
import { FilterTokens, QuickChips, SearchBox } from '@/lists/ListToolbar'
import { Popover } from '@/lists/Popover'
import { availableChips, localToday, searchColumn } from '@/lists/quickFilters'
import { useListState } from '@/lists/useListState'
import { DealBoard } from './DealBoard'

/**
 * Раздел «Сделки»: доска и таблица одного и того же (тикет #71, ТЗ §4.2; дизайн: Deals,
 * Kanban).
 *
 * ## Зачем экран поверх списка
 *
 * Семнадцать разделов из восемнадцати — это ровно `EntityListPage`. У сделок вторая форма
 * представления: канбан. Обе показывают одни и те же сделки, поэтому переключение между
 * ними не должно терять фильтр — иначе его набирают заново при каждом переключении и
 * переключаться перестают.
 *
 * ## Почему состояние живёт здесь, а не в списке
 *
 * `useListState` внутри `EntityListPage` умирает вместе с размонтированием таблицы. При
 * переключении на доску и обратно фильтры обнулились бы, причём молча. Поэтому состояние
 * поднято на уровень, который переживает оба режима, и передаётся вниз. Тулбар доски —
 * тот же поиск, те же чипы и тот же конструктор, что у таблицы, над тем же состоянием.
 *
 * ## Названо вслух: доска фильтрует не тем же механизмом
 *
 * Таблица фильтруется **на сервере** предикатом по всему каталогу колонок; доска отдаёт
 * готовый агрегат воронки, и фильтр к нему применяется на клиенте по тем полям, что
 * приехали на карточке. Совпадают они не полностью — см. `visibleCards` в `DealBoard`.
 * Направление расхождения выбрано видимое: доска покажет лишнюю карточку, а не спрячет
 * нужную. Поэтому на доске под условиями стоит пометка, что применены они частично.
 */

type Mode = 'table' | 'board'

interface DealsPageProps {
  entity: string
  resource: string
  titleKey: string
}

export function DealsPage({ entity, resource, titleKey }: DealsPageProps) {
  const { t, user } = useApp()
  // Таблица открывается первой: она отвечает на большинство вопросов к разделу
  // («найди сделку»), а доска — на вопрос о ходе работы.
  const [mode, setMode] = useState<Mode>('table')

  // Одно состояние на оба режима — то, ради чего этот компонент и существует.
  const list = useListState()

  // Тот же ключ, что у списка: описание колонок одно на оба режима и уже в кеше.
  const descriptorQuery = useQuery({
    queryKey: ['list-columns', entity],
    queryFn: ({ signal }) => fetchColumns(entity, signal),
    staleTime: 30 * 60 * 1000,
    retry: false,
    enabled: mode === 'board',
  })
  const descriptor = descriptorQuery.data
  const chips = useMemo(
    () =>
      descriptor
        ? availableChips(entity, descriptor.columns, {
            employeeId: user?.employeeId ?? null,
            userId: user?.userId ?? null,
            today: localToday(),
          })
        : [],
    [descriptor, entity, user?.employeeId, user?.userId],
  )
  const search = descriptor ? searchColumn(entity, descriptor.columns) : null

  const modeSwitch = (
    <div className="seg" role="group" aria-label={t('deals.mode')}>
      <button type="button" aria-pressed={mode === 'table'} onClick={() => setMode('table')}>
        <Icon name="lines" size="sm" />
        {t('deals.mode.table')}
      </button>
      <button type="button" aria-pressed={mode === 'board'} onClick={() => setMode('board')}>
        <Icon name="board" size="sm" />
        {t('deals.mode.board')}
      </button>
    </div>
  )

  if (mode === 'board') {
    const filters = list.state.filters
    return (
      <section className="entity-list">
        <div className="page-head">
          <h1 className="h1">{t(titleKey)}</h1>
          {modeSwitch}
          <span className="spacer" />
          {/* Заведение с доски (#100): право `create`, а не `read` — право смотреть доску
              не обещает права в неё добавить. */}
          {can(user, resource, 'create') && (
            <Link className="btn btn-primary" to="/deals/new">
              <Icon name="plus" />
              {t('deal.create')}
            </Link>
          )}
        </div>

        {descriptor && (
          <div className="toolbar" role="toolbar" aria-label={t('list.toolbar')}>
            {search && (
              <SearchBox
                column={search}
                descriptors={descriptor.columns}
                filters={filters}
                onChange={list.setFilters}
              />
            )}
            {chips.length > 0 && <div className="vsep" />}
            <QuickChips chips={chips} filters={filters} onChange={list.setFilters} />
            <Popover
              label={t('filter.title')}
              className="fbuilder-pop"
              trigger={
                <>
                  <Icon name="filter" size="sm" />
                  {t('filter.title')}
                  {filters.length > 0 && <span className="count-pill">{filters.length}</span>}
                </>
              }
            >
              <FilterBuilder
                entity={entity}
                descriptors={descriptor.columns}
                filters={filters}
                onChange={list.setFilters}
              />
            </Popover>
          </div>
        )}

        {descriptor && (
          <FilterTokens
            entity={entity}
            descriptors={descriptor.columns}
            filters={filters}
            chips={chips}
            onChange={list.setFilters}
          />
        )}

        {/* Условия на доске применяются по полям карточки, а не сервером: невидимое
            расхождение объясняло бы «пропавшие» карточки хуже, чем названное. */}
        {filters.length > 0 && (
          <p className="muted t-sm board-filters-note">
            {t('deals.board.filters', { count: filters.length })}
          </p>
        )}

        <DealBoard filters={filters} resource={resource} />
      </section>
    )
  }

  return (
    <EntityListPage
      entity={entity}
      resource={resource}
      titleKey={titleKey}
      listState={list}
      toolbar={modeSwitch}
    />
  )
}
